(ns nosco.ui.errors
  "Namespace for gracefully handling developer and network errors, showing error
  toasts and crashing the page.

  Developer errors are handled in two ways:
  - React Render errors are caught by the top-level ErrorBoundary in main.cljs
  - Uncaught exceptions in event handlers or in promises are caught via the global
    error handler and then also propagated to the react render loop and therefore
    caught by the ErrorBoundary

  Network errors are caught by using installing two default (overrideable) error
  handlers for `graphql-client/query-promise!` and `graphql-client/mutation-promise!`"
  (:require
   [clojure.string :as string]
   [goog :as g]
   [goog.object :as gobj]
   [goog.string :as gstring]
   [hx.hooks]
   [hx.react :as hx]
   [nosco.controllers :as controllers]
   [nosco.graphql.errors :as graphql-errors]
   [nosco.i18n.translate :as t]
   [nosco.react]
   [nosco.sentry :as sentry]
   [nosco.ui.animations :as animations]
   [nosco.ui.buttons :as buttons]
   [nosco.ui.buttons2 :as buttons2]
   [nosco.ui.text :as text]
   [nosco.ui.toast :as toast]
   [reseda.react]))

(defn extract-error-detail
  "Given a JS error, return a long string with details."
  [error]
  (let [name (.-name error)
        message (.-message error)
        stack (.-stack error)
        data (ex-data error)]
    (str name ": " message "\n"
         (when data (str (pr-str data) "\n"))
         "Stacktrace:\n"
         stack)))

(defn support-email-mailto
  "Create a mailto link that can be used to send a comprehensive error
  report to support@nos.co"
  [{:keys [error-id]}]
  (let [user (:user @nosco.controllers/app-state)
        email "support@nos.co"
        domain (-> js/document.location .-host)
        fullpath (-> js/document.location .-href)
        subject (str domain " support request")
        body (str "Please write your error report below the diagnostic information: \n"
                  "Site: " domain "\n"
                  "Path: " fullpath "\n"
                  (when error-id (str "ErrorID: " error-id "\n"))
                  (when user (str
                              "UserID: " (:id user) "\n"
                              "Email: " (:email user) "\n"))
                  "----------------------------\n"
                  "Error report: \n\n")
        mailto (str "mailto:" email
                    "?subject=" (js/encodeURIComponent subject)
                    "&body=" (js/encodeURIComponent body))]
    mailto))

(defn refresh-page
  []
  (.. js/window -location reload))

(defn crash-page-with-error!
  "Replace the entire UI with an error."
  [error]
  (js/console.log "Showing global error page" error)
  (js/setTimeout
   #(swap! controllers/app-state
           assoc :error error)))

(defn- show-error-toast-impl! [{:keys [title text error-id failure actions]
                                :or {actions [:refresh :support]}} mutation?]
  (let [title (if title title (t/t "Something went wrong"))
        text (if text text (get {:aborted (t/t "The request was aborted. Please try again.")
                                 :timeout (t/t "The request took too long. Please try again.")
                                 :error (t/t "There was a server error. Please try again.")
                                 :parse (t/t "The server sent an invalid reply. Please try again.")}
                                failure
                                (t/t "Something went wrong. Please try again.")))
        extra-actions (vec (remove keyword? actions))
        message {:title title
                 :error-id error-id
                 :text text
                 :actions (cond-> extra-actions
                            (contains? (set actions) :refresh)
                            (conj
                             {:type :button
                              :title (t/t "Refresh Page")
                              :on-click refresh-page})

                            (contains? (set actions) :support)
                            (conj
                             {:type :link
                              :href (support-email-mailto {:error-id error-id})
                              :title (t/t "Contact Support")}))}]
    (if mutation?
      (toast/show-message-error-mutation message)
      (toast/show-message-error message))))

(defn show-error-toast!
  "Show an error toast banner with the title, text and error-id.
  If text is not present, derive one based on the failure type (cljs-http).
  If title is not present, use `Something went wrong`

  Does not override the toasts displayed by show-error-toast-mutation!"
  [{:keys [title text error-id failure] :as toast}]
  (show-error-toast-impl! toast false))

(defn show-error-toast-mutation!
  "Show an error toast banner with the title, text and error-id.
  If text is not present, derive one based on the failure type (cljs-http).
  If title is not present, use `Something went wrong`

  Does not override the toasts displayed by show-error-toast!"
  [{:keys [title text error-id failure] :as toast}]
  (show-error-toast-impl! toast true))

(defn extract-graphql-error [response]
  (let [errors (:errors response)
        messages (map :message errors)]
    (when (seq messages)
      (string/join ", " messages))))

(defn capture-network-error-sentry
  "Capture a graphql error to sentry and return the error-id"
  [error-response]
  (let [{:keys [status status-text failure response original-text parse-error]} error-response
        _ (sentry/add-breadcrumb #js {:failure (str failure)
                                      :status status
                                      :original-text original-text
                                      :parse-error parse-error
                                      :status-text status-text
                                      :response (clj->js response)}
                                 {:type :error})
        format-error (fn [prefix suffix]
                       (str prefix ": (" status ") `" status-text "`"
                            (when suffix (str " [" suffix "]"))))
        error-string (cond
                       (= failure :parse)
                       (format-error "Parse error" nil)

                       (= failure :aborted)
                       (format-error "Aborted" nil)

                       (= failure :timeout)
                       (format-error "Timeout" nil)

                       (and (= failure :error) (some? response))
                       (format-error "Query error" (extract-graphql-error response))

                       (= failure :error)
                       (format-error "Generic Error" parse-error)

                       :else
                       (format-error "Unknown error" nil))
        error-id (sentry/capture-message error-string)]
    error-id))

(defn error-response->data [error-response]
  (let [status (:status error-response)
        response (:response error-response)
        unauthorized? (graphql-errors/response-errors response :unauthorized)
        not-found? (graphql-errors/response-errors response :not-found)
        duplicate? (graphql-errors/response-errors response :duplicate)
        log-sentry? (cond
                      unauthorized? false
                      duplicate? false
                      not-found? false
                      (= status 502) false
                      (= status 504) false
                      (= status 401) false
                      (= status 409) false
                      :else true)]
    {:response response
     :error-response error-response
     :status status
     :unauthorized? unauthorized?
     :not-found? not-found?
     :duplicate? duplicate?
     :log-sentry? log-sentry?}))

(defn- error->text [{:keys [unauthorized? not-found? duplicate?]}]
  (cond
    duplicate?
    (t/t "You cannot create this resource.")
    not-found?
    (t/t "The resource you tried to access was not found.")
    unauthorized?
    (t/t "You do not have permission to perform this action.")))

(defn default-mutation-error-handler
  "Mutations error fallback handler, if none is provided.
  Show a generic toast and resolve the promise so that the UI
  will continue as expected, ready for a retry."
  [error-response]
  (let [{:keys [response unauthorized? not-found? duplicate? log-sentry?] :as error-data}
        (error-response->data error-response)
        error-id (when log-sentry?
                   (capture-network-error-sentry error-response))]
    (show-error-toast-mutation! {:error-id error-id
                                 :text (error->text error-data)
                                 :title (cond
                                          duplicate?
                                          (t/t "__duplicate")
                                          not-found?
                                          (t/t "Not found")
                                          unauthorized?
                                          (t/t "Unauthorized"))
                                 :failure (:failure error-response)})
    response))

(defn default-query-error-handler
  "Queries default error handler. Will not log transient server errors to sentry, but
  will always crash the page."
  [error-response]
  (let [{:keys [status
                response
                unauthorized?
                not-found?
                duplicate?
                log-sentry?]
         :as error-response-data} (error-response->data error-response)
        error-id (when log-sentry?
                   (capture-network-error-sentry error-response))
        error-text (if status
                     (:status-text error-response)
                     (error->text error-response-data))]
    (throw (ex-info (gstring/format "Query Error (%s)" (or error-text "Unknown error"))
                    (merge error-response-data
                           {:ignore-sentry? true
                            :sentry-error-id error-id})))))

(defn- loading-when-error-promise [promise]
  (let [resolve-container #js {}
        no-error-p (js/Promise. (fn [resolve reject]
                                  (gobj/set resolve-container "resolve" resolve)))
        resolve (gobj/get resolve-container "resolve")]
    (.then promise
           (fn [value]
             (when-not (instance? js/Error value)
               (resolve value))
             value)
           (fn [e] e))
    no-error-p))

(defn suspending-value
  "Same then reseda.react/suspending-value but wraps the promise such that
  the promise goes into a unresolved state when an error occurs."
  [p]
  (reseda.react/suspending-value (loading-when-error-promise p)))

(defn react-dev-silly-error?
  "React will double-throw errors, is this one of those?
  Unfortunately not 100% reliable, it may have false negatives"
  [error]
  (and error
       (.-stack error)
       (string? (.-stack error))
       (string/includes? (.-stack error) "invokeGuardedCallback")))

(defrecord FriendlyError [heading description img link])
(defn make-friendly-error
  "Friendly errors are thrown at render time for expected failures like
   not-found or invalid state when the page cannot show its own alternative.
   They are presented via the FriendlyErrorBoundary in the user level."
  [{:keys [heading description img link]}]
  (->FriendlyError heading description img link))

(defn friendly-error? [error]
  (instance? FriendlyError error))

(defn attach-global-error-handler
  "Attach global error handlers for uncaught exceptions and unhandled promises.

  We redirect those into the React render tree so that the Sentry logic is
  consolidated in the top-level ErrorBoundary in nosco.views.main"
  []
  (js/window.addEventListener
   "unhandledrejection"
   (fn [^js e]
     (.preventDefault e)
     (js/console.log "Crashing due to unhandledrejection" e)
     (crash-page-with-error! (.-reason e))))
  (js/window.addEventListener
   "error"
   (fn [^js e]
      ;; in dev builds the window error handler is not reliable,
      ;; as it will trigger also for events that are actually handled
      ;; by ErrorBoundaries
      ;; See https://github.com/facebook/react/issues/10474
     (let [error (.-error e)]
       (js/console.log "Caught window error event" e)
       (cond

         (friendly-error? error)
         true ;; friendly errors are caught in a top-level error boundary

         (react-dev-silly-error? error)
         (do
           (js/console.log "Ignoring react dev error")
           true)

         goog.DEBUG
         (do
           (js/console.log "Should crash page entirely with error" error)
           (js/console.log "Bailing due to React dev mode flakiness")
           true)

         :else
         (do (crash-page-with-error! error)
             true))))))

(hx/defnc ErrorImage [{:keys [type]
                       :or {type :error}}]
  (case type
    :error [:img {:width "232px" :height "232px"
                  :src "/img/server-error.svg"}]
    :not-found [:img {:width "232px" :height "232px"
                      :src "/img/404.svg"}]))

(hx/defnc ErrorContent [{:keys [title text error-id actions detail] :as props}]
  [:div (nosco.react/merge-props
         {:class "container text-center pt-6 pb-6 rounded bg-white"
          :style {:max-height "100vh"}}
         (dissoc props :title :text :error-id :actions :detail))
   [ErrorImage {:type :error}]
   [text/Text {:variant text/Heading-Md
               :el :h1
               :class "mt-4 mb-4"}
    title]
   [text/Text {:variant text/Body-Lg-Regular
               :el :p
               :class "mt-4 mb-4 p-4"}
    text]
   (when actions
     [:<> actions])
   (let [[show-detail set-show-detail] (hx.hooks/useState false)]
     (when detail
       [:div
        [buttons/TextButton {:class "text-muted mt-6"
                             :label (if-not show-detail
                                      (t/t "Show details")
                                      (t/t "Hide details"))
                             :on-click #(set-show-detail not)}]
        [nosco.ui.animations/Collapse {:show show-detail}
         [:pre {:class "text-left font-size-xsm overflow-auto border border-light"
                :style {:max-height "8rem"}} detail]]]))
   (when error-id
     [text/Text {:variant text/Body-Sm-Regular :el :p
                 :class ["mb-0" (when-not detail "mt-6")]}
      "Error ID: " error-id])])

(hx/defnc ErrorSplash [props]
  [:div {:class "error-page2"}
   [ErrorContent props]])

(hx/defnc ContactSupport [{:keys [error-id]}]
  [:span {:class "mx-1"}
   [buttons2/Link
    {:label (t/t "Contact support")
     :variant :secondary
     :target "_blank"
     :rel "noopener noreferrer"
     :href (support-email-mailto {:error-id error-id})}]])

(hx/defnc RefreshPage []
  [:span {:class "mx-1"}
   [buttons2/Button
    {:label (t/t "Refresh page")
     :variant :primary
     :on-click refresh-page}]])

(hx/defnc ErrorMessage [{:keys [error error-id]}]
  [ErrorSplash
   {:title (t/t "Something went wrong")
    :actions [:<>
              [RefreshPage]
              [ContactSupport {:error-id error-id}]]
    :detail (extract-error-detail error)
    :error-id error-id
    :text (t/t-html "Please refresh the page and try again.<br>If the error persists, please contact support.")}])

(defn maybe-handle-sentry [error]
  (let [ignore-sentry? (-> error ex-data :ignore-sentry?)
        error-id (-> error ex-data :sentry-error-id)
        error-id (or error-id (when-not ignore-sentry? (sentry/capture-exception error)))]
    error-id))

(hx/defcomponent ErrorBoundary
  (constructor
    [this]
    (set! (. this -state)
          #js {:error nil
               :error-id nil})
    this)

  ^:static
  (getDerivedStateFromError
   (fn [error]
     (let [error-id (maybe-handle-sentry error)]
       #js {:error error
            :error-id error-id})))

  ;; we don't care for component did catch, as there's no way
  ;; to pass the error-id to the state to show it to the user
  ;; therefore we do the sentry error handling in getDerivedStateFromError
  #_(componentDidCatch
     [^js this error errorInfo]
     (if-let [on-catch (-> ^js this ^js .-props .-on-catch)]
       (on-catch error errorInfo)
       (js/console.error "ComponentDidCatch Error: " error
                         "\nComponent Stack" (.-componentStack errorInfo))))

  (render
    [^js this]
    (let [^js state (.-state this)
          ^js props (.-props this)
          error (gobj/get state "error")
          error-id (gobj/get state "error-id")]
      (if error
        [ErrorMessage {:error error
                       :error-id error-id}]
        (-> this .-props .-children)))))
